import { Typography } from "@mui/material";
import { Chess, type Square } from "chess.js";
import { MotifBadge } from "@/shared/components/MotifBadge";
import type { MotifGlyphKind } from "@/shared/components/MotifGlyph";
import { chessArrow, type ChessSquare, type ChessSquareStyles } from "@/shared/components/chessboard";
import type { BoardEvidenceItem } from "@/shared/components/chessboard/BoardEvidenceWheel";
import type { ReviewBoardSettings } from "./review-board-settings";
import { boardPositionMarkers } from "./review/position-markers";
import {
  POSITION_RELATIONSHIP_LEGEND,
  teachingMarkerHighlightSquares,
  type TeachingMarker,
} from "./review/teaching-explainer";

const glyphs: Record<TeachingMarker["kind"], [MotifGlyphKind, string]> = {
  pin: ["pin", "P"],
  relative_pin: ["pin", "P"],
  skewer: ["skewer", "S"],
  fork: ["fork", "F"],
  hanging: ["hanging_piece", "U"],
  support: ["text", "+"],
  xray: ["text", "X"],
  discovered_potential: ["discovered_attack", "D"],
  check: ["text", "+"],
};

export const RelationshipGlyph = ({ kind }: { kind: TeachingMarker["kind"] }) => (
  <MotifBadge kind={glyphs[kind][0]} label={glyphs[kind][1]} color={POSITION_RELATIONSHIP_LEGEND[kind].color} />
);

export const relationshipPresentation = (fen: string, settings: ReviewBoardSettings, focusSquare?: string | null) => {
  const markers = boardPositionMarkers(fen, { ...settings, showPositionMarkers: true }, focusSquare);
  const position = markers.length ? new Chess(fen) : null;
  const pieceAt = (square: string) => {
    const piece = position?.get(square as Square);
    const names = { p: "pawn", n: "knight", b: "bishop", r: "rook", q: "queen", k: "king" };
    return piece
      ? `${piece.color === "w" ? "White" : "Black"} ${names[piece.type]} on ${square}`
      : `piece on ${square}`;
  };
  const describe = (marker: TeachingMarker) => {
    const from = pieceAt(marker.from),
      to = pieceAt(marker.to),
      subject = marker.subject ? pieceAt(marker.subject) : "The intervening piece";
    switch (marker.kind) {
      case "pin":
        return `${subject} shields ${to} from ${from}. Moving it off this line would expose check.`;
      case "relative_pin":
        return `${subject} shields ${to} from ${from}. Moving it off this line may expose the more valuable piece.`;
      case "skewer":
        return `${from} attacks ${subject}, with ${to} behind it. This alignment does not prove a winning tactic.`;
      case "fork":
        return `${from} attacks multiple pieces, including ${to}. This does not prove a material gain.`;
      case "hanging":
        return `${from} attacks ${to}, which has no geometric defenders. A safe capture is not guaranteed.`;
      case "support":
        return `${from} guards ${to}. A pinned defender may be unable to capture.`;
      case "xray":
        return `${from} lines up with ${to} through ${subject}. The intervening piece currently blocks the line.`;
      case "discovered_potential":
        return `Moving ${subject} can uncover an attack by ${from} on ${to}. This is potential, not a played tactic.`;
      case "check":
        return `${from} checks ${to}.`;
    }
  };
  const visible = settings.showPositionMarkers ? markers : [];
  const items: BoardEvidenceItem[] = settings.showTacticalMotifs
    ? markers.map((marker) => {
        const { label, color } = POSITION_RELATIONSHIP_LEGEND[marker.kind];
        const detail = describe(marker);
        return {
          id: `position:relationship:${marker.kind}:${marker.from}:${marker.to}`,
          relationship: marker.kind,
          kind: "motif",
          square: marker.subject ?? (["support", "hanging"].includes(marker.kind) ? marker.to : marker.from),
          label,
          color,
          description: detail,
          playable: false,
          highlightFen: fen,
          highlightSquares: marker.kind === "hanging" ? [marker.to] : teachingMarkerHighlightSquares(marker),
          glyph: <RelationshipGlyph kind={marker.kind} />,
          disclosure: (
            <>
              <Typography variant="overline">BOARD RELATIONSHIP</Typography>
              <Typography variant="subtitle1">{label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {detail}
              </Typography>
            </>
          ),
        };
      })
    : [];
  return {
    items,
    arrows: visible.map((marker) => ({
      ...chessArrow(marker.from as ChessSquare, marker.to as ChessSquare, marker.color),
      label: POSITION_RELATIONSHIP_LEGEND[marker.kind].label,
      description: describe(marker),
    })),
    squareStyles: Object.fromEntries(
      visible.flatMap((marker) =>
        teachingMarkerHighlightSquares(marker).map((square) => [
          square,
          { backgroundImage: `linear-gradient(${marker.tint}, ${marker.tint})` },
        ]),
      ),
    ) as ChessSquareStyles,
  };
};

export const mergeRelationshipEvidence = (evidence: BoardEvidenceItem[], relationships: BoardEvidenceItem[]) => {
  const result = [...evidence];
  for (const item of relationships) {
    const index = result.findIndex(
      (existing) =>
        existing.moveScope === item.moveScope &&
        existing.square === item.square &&
        existing.relationship === item.relationship,
    );
    if (index < 0) result.push(item);
    else {
      const existing = result[index];
      result[index] = {
        ...existing,
        color: item.color,
        glyph: item.glyph,
        description: existing.playable ? `${existing.description} ${item.description}` : item.description,
        disclosure: existing.playable ? (
          <>
            {existing.disclosure}
            <Typography variant="body2">{item.description}</Typography>
          </>
        ) : (
          item.disclosure
        ),
      };
    }
  }
  return result;
};
